<template>
  <form @submit.prevent="handleSubmit" class="space-y-6">
    <!-- Basic Information Card -->
    <div class="bg-white rounded-lg border border-slate-200 shadow-sm">
      <div class="px-6 py-4 border-b border-slate-100">
        <h3 class="text-lg font-semibold text-slate-800 mb-0">Podstawowe informacje</h3>
      </div>
      <div class="px-6 pb-6 space-y-4">
        <!-- Nazwa -->
        <div class="space-y-2">
          <label for="offerName" class="text-sm font-medium text-slate-700">
            Nazwa
          </label>
          <input 
            id="offerName" 
            v-model.trim="state.name" 
            type="text" 
            class="w-full h-11 px-3 py-2 bg-white border border-slate-300 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors" 
            required 
          />
        </div>

        <!-- Produkt -->
        <div class="space-y-2">
          <label class="text-sm font-medium text-slate-700">Produkt</label>
          <ProductSelector
            :product-id="state.product_id"
            :display-name="productName"
            @select="selectProduct"
            @clear="clearProduct"
          />
        </div>

        <!-- Opis -->
        <div class="space-y-2">
          <label for="offerDesc" class="text-sm font-medium text-slate-700">
            Opis
          </label>
          <textarea 
            id="offerDesc" 
            v-model.trim="state.description" 
            class="w-full min-h-32 px-3 py-2 bg-white border border-slate-300 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors resize-none"
            rows="3"
          ></textarea>
        </div>
      </div>
    </div>

    <!-- Category and Price Card -->
    <div class="bg-white rounded-lg border border-slate-200 shadow-sm">
      <div class="p-6">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div class="space-y-2">
            <label class="text-sm font-medium text-slate-700">Kategoria</label>
            <CategorySelector
              :display-name="categoryName"
              @select="selectCategory"
            />
          </div>

          <div class="space-y-2">
            <label for="offerPrice" class="text-sm font-medium text-slate-700">
              Cena
            </label>
            <div class="relative">
              <input 
                id="offerPrice" 
                v-model="state.price" 
                type="text" 
                class="w-full h-11 px-3 py-2 pr-16 bg-white border border-slate-300 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors" 
                placeholder="np. 123,45" 
              />
              <div class="absolute right-3 top-1/2 -translate-y-1/2">
                <span class="text-sm font-medium text-slate-600 bg-slate-100 px-2 py-1 rounded">
                  PLN
                </span>
              </div>
            </div>
            <p class="text-xs text-slate-500">Pole opcjonalne. Użyj przecinka lub kropki.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Features Card -->
    <div class="bg-white rounded-lg border border-slate-200 shadow-sm">
      <div class="px-6 py-4 border-b border-slate-100">
        <h3 class="text-lg font-semibold text-slate-800 mb-0">Cechy produktu</h3>
      </div>
      <div class="px-6 pb-6">
        <FeatureSelector
          v-model="state.feature_values"
        />
      </div>
    </div>

    <!-- Images Card -->
    <div class="bg-white rounded-lg border border-slate-200 shadow-sm">
      <div class="px-6 py-4 border-b border-slate-100">
        <h3 class="text-lg font-semibold text-slate-800 mb-0">Zdjęcia</h3>
      </div>
      <div class="px-6 pb-6">
        <ImageUploader 
          ref="uploaderRef" 
          :offer-id="state.id || null" 
          :key="`uploader-${state.id ?? 'new'}`" 
        />
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="flex gap-3 pt-4">
      <button 
        class="px-6 py-2.5 border border-slate-300 text-slate-700 bg-white rounded-md hover:border-slate-400 transition-colors font-medium disabled:opacity-50" 
        type="button" 
        @click="$emit('cancel')" 
        :disabled="loading"
      >
        Anuluj
      </button>
      <button 
        class="px-8 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium shadow-sm disabled:opacity-50 flex items-center gap-2" 
        type="submit" 
        :disabled="loading"
      >
        <span 
          v-if="loading" 
          class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
        ></span>
        Zapisz
      </button>
    </div>
  </form>
</template>
<script setup lang="ts">
import { watch, onMounted } from 'vue';
import type { OfferDto, OfferFormData } from '../types/offer.types';
import ImageUploader from '../../../components/ImageUploader.vue';
import ProductSelector from './ProductSelector.vue';
import CategorySelector from './CategorySelector.vue';
import FeatureSelector from './FeatureSelector.vue';
import { useOfferForm } from '../composables/useOfferForm';
import { useOfferValidation } from '../composables/useOfferValidation';
import { useImageUploader } from '../composables/useImageUploader';

const props = defineProps<{ 
  offer: OfferDto | null; 
  loading: boolean; 
}>();

const emit = defineEmits<{ 
  save: [data: OfferFormData]; 
  cancel: []; 
}>();

const { 
  state, 
  productName, 
  categoryName,
  applyOfferToForm,
  selectCategory,
  selectProduct,
  clearProduct,
  getFormData 
} = useOfferForm(props.offer);

const { getFirstError } = useOfferValidation();

const { 
  uploaderRef, 
  applyOfferImages, 
  saveImageOrder, 
  uploadAllImages 
} = useImageUploader();

watch(() => props.offer, (offer) => {
  applyOfferToForm(offer);
  applyOfferImages(offer);
});

watch(() => uploaderRef.value, (uploader) => {
  if (uploader) {
    applyOfferImages(props.offer);
  }
});

onMounted(() => {
  applyOfferToForm(props.offer);
  setTimeout(() => {
    applyOfferImages(props.offer);
  }, 100);
});

async function handleSubmit() {
  const formData = getFormData();
  const error = getFirstError(formData);
  
  if (error) {
    alert(error);
    return;
  }
  
  emit('save', formData);
}

defineExpose({ 
  saveOrder: saveImageOrder, 
  uploadAll: uploadAllImages 
});
</script>
